import { useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { Trash2, Paperclip, ChevronDown, ChevronUp } from 'lucide-react'
import CategoryBadge from './CategoryBadge'
import VoteButtons from './VoteButtons'
import IdeaAttachmentPanel from './IdeaAttachmentPanel'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'

function formatAttachmentLabel(count) {
  return count === 1 ? '1 attachment' : `${count} attachments`
}

function formatAttachmentTeaser(attachments) {
  if (!attachments.length) return ''
  const first = attachments[0].original_filename || 'File'
  if (attachments.length === 1) return first
  return `${first} and ${attachments.length - 1} more`
}

export default function SuggestionCard({
  suggestion,
  score,
  userVote,
  onVote,
  canDelete,
  onDelete,
  deleting,
  attachments = [],
}) {
  const [expanded, setExpanded] = useState(false)
  const desc = suggestion.description || ''
  const isTruncated = desc.length > 180 && !expanded

  return (
    <div
      className="bg-white rounded-xl border border-border shadow-sm hover:shadow-md hover:border-border/80 transition-all cursor-pointer"
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex gap-4 p-5">
        <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <VoteButtons
            score={score}
            userVote={userVote}
            onVote={(val) => onVote(suggestion.id, val)}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <CategoryBadge category={suggestion.category} />
            {attachments.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-border bg-secondary/80 text-foreground">
                <Paperclip className="w-3 h-3" />
                {formatAttachmentLabel(attachments.length)}
              </span>
            )}
          </div>
          <h3 className="font-semibold text-[15px] text-foreground leading-snug mb-1.5">
            {suggestion.title}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {isTruncated ? `${desc.slice(0, 180)}…` : desc}
          </p>
          {isTruncated && (
            <span className="text-xs text-primary font-medium mt-1 inline-block">
              Click to read more
            </span>
          )}
          {attachments.length > 0 && !expanded && (
            <div className="mt-3 rounded-lg border border-dashed border-primary/30 bg-primary/5 px-3 py-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground flex items-center gap-1.5">
                    <Paperclip className="w-3.5 h-3.5 shrink-0 text-primary" />
                    <span className="truncate">
                      {formatAttachmentLabel(attachments.length)} · {formatAttachmentTeaser(attachments)}
                    </span>
                  </p>
                  <p className="text-xs text-primary font-medium mt-1">Click to view files</p>
                </div>
                <ChevronDown className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              </div>
            </div>
          )}
          {expanded && attachments.length > 0 && (
            <div className="mt-4 pt-3 border-t border-border" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-end mb-1">
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <ChevronUp className="w-3.5 h-3.5" />
                  Click card to collapse
                </span>
              </div>
              <IdeaAttachmentPanel attachments={attachments} showDownload />
            </div>
          )}
          <div className="flex items-center justify-between mt-2 gap-3">
            {suggestion.created_at && (
              <p className="text-xs text-muted-foreground">
                Submitted{' '}
                {formatDistanceToNow(new Date(suggestion.created_at), { addSuffix: true })}
              </p>
            )}
            {canDelete && (
              <div onClick={(e) => e.stopPropagation()}>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-muted-foreground hover:text-destructive"
                      disabled={deleting}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete your idea?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently remove your idea, all attachments, and all votes on
                        it. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => onDelete(suggestion.id)}
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
